import { Dealership } from "@/app/_components/context/dealership-context-wrapper";
import { Card, Checkbox } from "@material-tailwind/react";
import { useEffect, useMemo, useState } from "react";

export enum LeaderboardType {
  SALES = "SALES",
  MANAGER = "MANAGER",
  FINANCE = "FINANCE",
}

type ManagerLeaderboardData = {
  [key: string]: {
    units: number;
    newGross: number;
    usedGross: number;
    totalGross: number;
  };
};

type SalesLeaderboardData = {
  [key: string]: {
    newUnits: number;
    usedUnits: number;
    totalUnits: number;
  };
};

interface LeaderboardCardProps {
  currentDealership: Dealership | undefined;
  gtgOnly: boolean;
  primaryUserGroup: string | undefined;
}

export const LeaderboardCard = ({
  gtgOnly,
  primaryUserGroup,
  currentDealership,
}: LeaderboardCardProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [leaderboardRange, setLeaderboardRange] = useState<
    "MTD" | "WTD" | "YTD"
  >("MTD");
  const [leaderboardData, setLeaderboardData] = useState<
    ManagerLeaderboardData | SalesLeaderboardData
  >();
  const [leaderboardType, setLeaderboardType] = useState<LeaderboardType>(
    LeaderboardType.SALES,
  );

  const labels = {
    SALES: "Sales Person Leaderboard",
    MANAGER: "Sales Manager Leaderboard",
    FINANCE: "Finance Manager Leaderboard",
  };

  const kFormatter = (num: number) => {
    return Math.abs(num) > 999
      ? Math.sign(num) * Number((Math.abs(num) / 1000).toFixed(1)) + "K"
      : Math.sign(num) * Math.abs(num);
  };

  useEffect(() => {
    if (!currentDealership) {
      return;
    }

    const fetchLeaderboardData = async () => {
      if (
        leaderboardType === LeaderboardType.FINANCE ||
        leaderboardType === LeaderboardType.MANAGER
      ) {
        const response = await fetch(
          `/v2/api/reporting/managers-leaderboard?range=${leaderboardRange}&gtg=${gtgOnly}&type=${leaderboardType}`,
        );
        if (!response.ok) {
          console.error(response.statusText);
          return;
        }
        const data: ManagerLeaderboardData = await response.json();

        const rankedData: ManagerLeaderboardData = Object.entries(data)
          .sort((a, b) => {
            return b[1].totalGross - a[1].totalGross;
          })
          .reduce((acc, [key, value], index) => {
            acc[key] = value;
            return acc;
          }, {});

        setLeaderboardData(rankedData);
      } else {
        const response = await fetch(
          `/v2/api/reporting/sales-leaderboard?range=${leaderboardRange}&gtg=${gtgOnly}`,
        );
        if (!response.ok) {
          console.error(response.statusText);
          return;
        }
        const data: SalesLeaderboardData = await response.json();

        const rankedData: SalesLeaderboardData = Object.entries(data)
          .sort((a, b) => {
            return b[1].totalUnits - a[1].totalUnits;
          })
          .reduce((acc, [key, value], index) => {
            acc[key] = value;
            return acc;
          }, {});

        setLeaderboardData(rankedData);
      }
    };

    fetchLeaderboardData();
  }, [leaderboardType, leaderboardRange, gtgOnly, currentDealership]);

  const dropdownButtonComponent = useMemo(() => {
    if (primaryUserGroup === "Sales Person") {
      return (
        <span className="font-bold text-black">
          {labels[LeaderboardType.SALES]}
        </span>
      );
    }

    return (
      <div className="relative">
        <button
          className="flex items-center h-8 pr-2 focus:outline-none"
          onClick={() => {
            setDropdownOpen(!dropdownOpen);
          }}
        >
          <span className="font-bold text-black">
            {labels[leaderboardType]}
          </span>
          <svg
            className="w-4 h-4 mt-px ml-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {dropdownOpen && (
          <div className="absolute flex flex-col w-60 shadow-lg bg-white">
            <a
              className="flex items-center h-8 px-3 text-sm hover:bg-gray-200"
              href="#"
              onClick={() => {
                setLeaderboardType(LeaderboardType.FINANCE);
                setDropdownOpen(false);
              }}
            >
              Finance Manager Leaderboard
            </a>
            <a
              className="flex items-center h-8 px-3 text-sm hover:bg-gray-200"
              href="#"
              onClick={() => {
                setLeaderboardType(LeaderboardType.MANAGER);
                setDropdownOpen(false);
              }}
            >
              Sales Manager Leaderboard
            </a>
            <a
              className="flex items-center h-8 px-3 text-sm hover:bg-gray-200"
              href="#"
              onClick={() => {
                setLeaderboardType(LeaderboardType.SALES);
                setDropdownOpen(false);
              }}
            >
              Sales Person Leaderboard
            </a>
          </div>
        )}
      </div>
    );
  }, [dropdownOpen, leaderboardType, labels, primaryUserGroup]);

  return (
    <Card className="w-1/2 rounded-sm shadow-none outline outline-1 outline-gray-200 max-h-[500px]">
      <div className="px-8 py-4">
        <div className="flex flex-row items-center justify-between border-b">
          <div className="font-bold text-black">{dropdownButtonComponent}</div>
          <div>
            <Checkbox
              className="checked:bg-[#43d662]"
              onChange={() => {
                setLeaderboardRange("MTD");
              }}
              checked={leaderboardRange === "MTD"}
              label={<span className="font-bold text-black">MTD</span>}
            />
            <Checkbox
              className="checked:bg-[#43d662]"
              checked={leaderboardRange === "YTD"}
              onChange={() => {
                setLeaderboardRange("YTD");
              }}
              label={<span className="font-bold text-black">YTD</span>}
            />
            <Checkbox
              className="checked:bg-[#43d662]"
              onChange={() => {
                setLeaderboardRange("WTD");
              }}
              checked={leaderboardRange === "WTD"}
              label={<span className="font-bold text-black">WTD</span>}
            />
          </div>
        </div>
        <div className="h-[30vh] overflow-auto">
          <table key={leaderboardType} className="w-full">
            <thead>
              {leaderboardType === LeaderboardType.SALES ? (
                <tr>
                  <th className="text-center">Rank</th>
                  <th className="text-left">Name</th>
                  <th className="text-center">New </th>
                  <th className="text-center">Used</th>
                  <th className="text-center">Total</th>
                </tr>
              ) : (
                <tr>
                  <th className="text-center">Rank</th>
                  <th className="text-left">Name</th>
                  <th className="text-center">Units</th>
                  <th className="text-right">New </th>
                  <th className="text-right">Used</th>
                  <th className="text-right">Total</th>
                </tr>
              )}
            </thead>
            <tbody>
              {leaderboardData &&
                Object.entries(leaderboardData).map(([key, value], index) => {
                  return leaderboardType === LeaderboardType.SALES ? (
                    <tr
                      style={
                        index == 0
                          ? {
                              color: "#43d662",
                            }
                          : {}
                      }
                    >
                      <td className="text-center">{index + 1}</td>
                      <td className="text-left">{key}</td>
                      <td className="text-center">{value.newUnits}</td>
                      <td className="text-center">{value.usedUnits}</td>
                      <td className="text-center">{value.totalUnits}</td>
                    </tr>
                  ) : (
                    <tr
                      style={
                        index == 0
                          ? {
                              color: "#43d662",
                            }
                          : {}
                      }
                    >
                      <td className="text-center">{index + 1}</td>
                      <td className="text-left">{key}</td>
                      <td className="text-center">{value.units}</td>
                      <td className="text-right">
                        ${kFormatter(value.newGross)}
                      </td>
                      <td className="text-right">
                        ${kFormatter(value.usedGross)}
                      </td>

                      <td className=" text-right">
                        ${kFormatter(value.totalGross)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

export default LeaderboardCard;
